import { useParams, Link } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <p className="text-foreground-500 mb-4">文章未找到</p>
            <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.categorySlug === article.categorySlug && a.slug !== article.slug)
    .slice(0, 3);

  const breadcrumbItems = [
    { label: '首页', path: '/' },
    { label: article.categoryName, path: `/category/${article.categorySlug}` },
    { label: article.title },
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: { '@type': 'Person', name: article.author },
    datePublished: article.publishDate,
    url: `https://fulidaohang.com/article/${article.slug}`,
    inLanguage: 'zh-CN',
    publisher: {
      '@type': 'Organization',
      name: '福利导航',
      url: 'https://fulidaohang.com',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://fulidaohang.com/article/${article.slug}`,
    },
    keywords: article.tags.join(','),
  };

  const breadcrumbLD = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://fulidaohang.com' },
      { '@type': 'ListItem', position: 2, name: article.categoryName, item: `https://fulidaohang.com/category/${article.categorySlug}` },
      { '@type': 'ListItem', position: 3, name: article.title },
    ],
  };

  return (
    <>
      <Navbar />
      <title>{article.title} | 福利导航</title>
      <meta name="description" content={article.excerpt} />
      <meta name="keywords" content={article.tags.join(',')} />
      <link rel="canonical" href={`https://fulidaohang.com/article/${article.slug}`} />
      <meta property="og:title" content={`${article.title} | 福利导航`} />
      <meta property="og:description" content={article.excerpt} />
      <meta property="og:type" content="article" />
      <meta property="og:url" content={`https://fulidaohang.com/article/${article.slug}`} />
      <meta property="og:image" content={article.coverImage} />
      <meta property="article:published_time" content={article.publishDate} />
      <meta property="article:author" content={article.author} />
      <meta property="article:section" content={article.categoryName} />
      {article.tags.map((tag) => (
        <meta key={tag} property="article:tag" content={tag} />
      ))}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={`${article.title} | 福利导航`} />
      <meta name="twitter:description" content={article.excerpt} />
      <meta name="twitter:image" content={article.coverImage} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLD) }} />

      <article className="pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-3xl mx-auto px-4 md:px-6 py-10 md:py-14">
            <div className="mb-3">
              <Breadcrumb items={breadcrumbItems} />
            </div>
            <div className="flex items-center gap-2 mb-4">
              <Link
                to={`/category/${article.categorySlug}`}
                className="px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium hover:bg-primary-200 transition-colors whitespace-nowrap"
              >
                {article.categoryName}
              </Link>
              <span className="text-xs text-foreground-400">{article.publishDate}</span>
              <span className="text-xs text-foreground-400">{article.readTime}</span>
            </div>
            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 leading-tight">
              {article.title}
            </h1>
            <p className="mt-4 text-foreground-600 leading-relaxed">
              {article.excerpt}
            </p>
            <div className="flex items-center gap-3 mt-6 pt-6 border-t border-background-200/70">
              <span className="w-9 h-9 rounded-full bg-secondary-200 flex items-center justify-center">
                <i className="ri-user-line text-secondary-700 text-sm"></i>
              </span>
              <div>
                <p className="text-sm font-medium text-foreground-800">{article.author}</p>
                <p className="text-xs text-foreground-500">福利导航编辑团队</p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-4 md:px-6 py-8 md:py-12">
          <div className="mb-8 rounded-xl overflow-hidden">
            <img
              src={article.coverImage}
              alt={article.title}
              title={`${article.title} - 福利导航`}
              className="w-full h-auto"
            />
          </div>

          <div
            className="prose-custom text-foreground-800 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="flex flex-wrap gap-2 mt-10 pt-8 border-t border-background-200/70">
            {article.tags.map((tag) => (
              <span key={tag} className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-sm whitespace-nowrap">
                #{tag}
              </span>
            ))}
          </div>

          {relatedArticles.length > 0 && (
            <div className="mt-12 pt-10 border-t border-background-200/70">
              <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-5">
                相关福利推荐
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {relatedArticles.map((rel) => (
                  <Link
                    key={rel.slug}
                    to={`/article/${rel.slug}`}
                    className="group bg-background-100 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer"
                  >
                    <div className="h-36 overflow-hidden">
                      <img
                        src={rel.coverImage}
                        alt={rel.title}
                        title={rel.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-3">
                      <h4 className="font-heading text-sm font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {rel.title}
                      </h4>
                      <p className="text-xs text-foreground-500 mt-1">{rel.publishDate}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </article>

      <Footer />
    </>
  );
}